import React, { useState } from 'react';
import { usePrivacy } from '../contexts/PrivacyContext';
import { useProjects } from '../contexts/ProjectContext';
import { StatusBadge } from '../components/common/StatusBadge';
import { formatDate } from '../lib/utils';
import { Job } from '@kaizen/shared';
import { History, Terminal, X, CheckCircle, XCircle } from 'lucide-react';

const INITIAL_HISTORY: Job[] = [
  {
    id: 'job-101',
    userId: '00000000-0000-0000-0000-000000000001',
    deviceId: '11111111-1111-1111-1111-111111111111',
    projectId: '7d84a284-06c6-4a1c-88b7-11883085930c',
    type: 'diagnostics',
    status: 'succeeded',
    payload: { command: 'Get-HostDiagnostics' },
    workingDirectory: 'D:\\Enabling Work Module\\kaizen-enabling-works-web',
    startedAt: new Date(Date.now() - 7200000).toISOString(),
    finishedAt: new Date(Date.now() - 7196000).toISOString(),
    exitCode: 0,
    createdAt: new Date(Date.now() - 7200000).toISOString()
  },
  {
    id: 'job-102',
    userId: '00000000-0000-0000-0000-000000000001',
    deviceId: '11111111-1111-1111-1111-111111111111',
    projectId: '7d84a284-06c6-4a1c-88b7-11883085930c',
    type: 'build',
    status: 'succeeded',
    payload: { command: 'npm run build' },
    workingDirectory: 'D:\\Enabling Work Module\\kaizen-enabling-works-web',
    startedAt: new Date(Date.now() - 3600000).toISOString(),
    finishedAt: new Date(Date.now() - 3550000).toISOString(),
    exitCode: 0,
    createdAt: new Date(Date.now() - 3600000).toISOString()
  }
];

export const HistoryPage: React.FC = () => {
  const { displayPath } = usePrivacy();
  const { projects } = useProjects();
  const [jobs] = useState<Job[]>(INITIAL_HISTORY);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <History className="w-5 h-5 text-indigo-600" />
          <span>Execution History</span>
        </h2>
        <p className="text-xs text-slate-500">
          Audit trail of jobs dispatched and executed on the personal laptop host
        </p>
      </div>

      <div className="acrylic-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider text-[10px]">
                <th className="px-5 py-3">Time</th>
                <th className="px-5 py-3">Job Type</th>
                <th className="px-5 py-3">Project</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Exit Code</th>
                <th className="px-5 py-3 text-right">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {jobs.map((job) => (
                <tr
                  key={job.id}
                  onClick={() => setSelectedJob(job)}
                  className="hover:bg-slate-50/60 cursor-pointer transition-colors"
                >
                  <td className="px-5 py-3 text-slate-600">
                    {formatDate(job.startedAt || job.createdAt)}
                  </td>
                  <td className="px-5 py-3 font-mono font-medium text-slate-900">
                    {job.type}
                  </td>
                  <td className="px-5 py-3 text-slate-700">
                    {projects.find(p => p.id === job.projectId)?.name || 'General'}
                  </td>
                  <td className="px-5 py-3">
                    <StatusBadge status={job.status} />
                  </td>
                  <td className="px-5 py-3 font-mono">
                    {job.exitCode !== null && job.exitCode !== undefined ? (
                      <span className={`inline-flex items-center gap-1 text-[11px] ${
                        job.exitCode === 0 ? 'text-emerald-700 font-semibold' : 'text-rose-600 font-semibold'
                      }`}>
                        {job.exitCode === 0 ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {job.exitCode}
                      </span>
                    ) : (
                      <span className="text-slate-400">--</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right text-indigo-600 font-medium hover:underline">
                    View
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Job Detail Drawer */}
      {selectedJob && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md h-full shadow-2xl p-6 flex flex-col justify-between overflow-y-auto">
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-indigo-600" />
                  <h3 className="font-bold text-sm text-slate-900">Job Details: {selectedJob.id}</h3>
                </div>
                <button
                  onClick={() => setSelectedJob(null)}
                  className="p-1 text-slate-400 hover:text-slate-600 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-2 text-xs">
                <div className="flex justify-between py-1 border-b border-slate-50">
                  <span className="text-slate-400">Type:</span>
                  <span className="font-mono font-medium text-slate-800">{selectedJob.type}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-50">
                  <span className="text-slate-400">Status:</span>
                  <StatusBadge status={selectedJob.status} />
                </div>
                <div className="flex justify-between py-1 border-b border-slate-50">
                  <span className="text-slate-400">Exit Code:</span>
                  <span className="font-mono font-semibold text-slate-800">{selectedJob.exitCode ?? '--'}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-50">
                  <span className="text-slate-400">Started:</span>
                  <span className="text-slate-700">{formatDate(selectedJob.startedAt)}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-50">
                  <span className="text-slate-400">Finished:</span>
                  <span className="text-slate-700">{formatDate(selectedJob.finishedAt)}</span>
                </div>
                <div>
                  <span className="text-slate-400 block mb-1">Working Directory:</span>
                  <span className="font-mono text-[11px] text-slate-700 block bg-slate-50 p-2 rounded border border-slate-100">
                    {displayPath(selectedJob.workingDirectory)}
                  </span>
                </div>
              </div>

              <div>
                <span className="text-xs font-semibold text-slate-700 block mb-1">Command Executed:</span>
                <div className="p-3 bg-slate-950 text-slate-200 font-mono text-xs rounded-lg overflow-x-auto">
                  <code>{selectedJob.payload.command || 'Direct Execution'}</code>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <button
                onClick={() => setSelectedJob(null)}
                className="w-full py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition-colors"
              >
                Close Drawer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
